import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { LucideIcon } from "lucide-react"
import { taskSubtypeApi } from "@/api/taskSubtypeApi"
import { getTaskTypeIcon, getTaskTypeIconBg } from "@/utils/taskTypeIcons"
import type { TaskSubtype } from "@/types/task-subtype"
import { useLocalizedSystemLookup } from "@/hooks/useLocalizedSystemLookup"
import { useLocale } from "@/hooks/useLocale"
import { localizeSubtype, type Localized } from "@/utils/subtypeLabel"
import { onResourceVersionChange } from "@/utils/configVersionEvents"

/** A task_type lookup row (from `useSystemLookup('task_type')`). */
interface TaskTypeOption {
    code: string
    label: string
}

/** One task type and the (active) subtypes that belong under it. */
export interface TaskCategoryGroup {
    /** task_type code, e.g. `cleaning`. */
    typeCode: string
    /** Human label for the type. */
    typeLabel: string
    icon: LucideIcon
    /** Tailwind bg class for the icon chip (e.g. `bg-blue-100 dark:bg-blue-950/50`). */
    iconBg: string
    /** Tailwind text class for the icon glyph (e.g. `text-blue-600`). */
    iconFg: string
    /** Active subtypes ordered by sortOrder. Empty when the type has none. */
    subtypes: Localized<TaskSubtype>[]
}

/** Nova `POST /<entity>/search` response (post axios-unwrap). */
interface SearchResponse<T> {
    results: T[]
    pagination?: {
        page: number
        pageSize: number
        totalPages: number
        totalCount: number
    }
}

/**
 * Icon glyph color per task type — pairs with `getTaskTypeIconBg`'s bg-*-100
 * chip. Kept local (the shared util only exposes the bg) so the combo's group
 * headers read like the mockup. Unknown types fall back to slate.
 */
const TASK_TYPE_ICON_FG: Record<string, string> = {
    cleaning: "text-blue-600",
    maintenance: "text-orange-600",
    inspection: "text-indigo-600",
    delivery: "text-emerald-600",
    admin: "text-muted-foreground",
    administrative: "text-muted-foreground",
    special: "text-yellow-600",
    guest_service: "text-purple-600",
    guest_request: "text-pink-600",
    "check-in": "text-green-600",
    "check-out": "text-red-600",
    other: "text-muted-foreground",
}

const getTaskTypeIconFg = (type: string): string =>
    TASK_TYPE_ICON_FG[type] ?? TASK_TYPE_ICON_FG.other

// 模块级缓存 + in-flight 去重（5 分钟）：看板 4 处 useIntentLabels + 表单 + 详情各自 mount 一次
// 本 hook，没缓存会每次都打一遍 /task-subtypes/search（subtype 低基数、读多写极少）。
const CACHE_TTL_MS = 5 * 60 * 1000
let subtypeCache: { rows: TaskSubtype[]; at: number } | null = null
let subtypeInflight: Promise<TaskSubtype[]> | null = null
// 缓存的「代」。clearTaskCategoryCache 每清一次 +1,在途请求各自带着发起时的代号:
// 代号对不上就既不写回 subtypeCache(否则登出/401 清完之后,上一个租户迟到的响应又把
// 它的 subtype 写回来,下一个用户 5 分钟内直接命中),也不清 subtypeInflight(那已经是
// 下一个用户新发的那次)。
let subtypeCacheGen = 0

async function loadAllSubtypes(): Promise<TaskSubtype[]> {
    if (subtypeCache && Date.now() - subtypeCache.at < CACHE_TTL_MS) return subtypeCache.rows
    if (subtypeInflight) return subtypeInflight
    const gen = subtypeCacheGen
    subtypeInflight = (async () => {
        const res = (await taskSubtypeApi.search({
            filters: {
                isEnabled: { type: "boolean", value: true },
            },
            // Default projection omits JSONB schema columns; request
            // inputFormSchema explicitly so the form can render the
            // selected subtype's Input Form. Comma string — DSL rejects arrays.
            // tenantId: 默认 subtype 租户行优先(pickDefaultSubtype,与后端 get_default_by_task_type 同口径)
            // labelI18n:subtype 名按界面语言显示(groups 里套 localizeSubtype)
            columns: "id,code,label,labelI18n,iconUrl,isDefault,isEnabled,sortOrder,taskTypeCode,tenantId,inputFormSchema",
            orderBy: "sortOrder",
            page: 1,
            pageSize: 500,
        })) as unknown as SearchResponse<TaskSubtype>
        const rows = res?.results ?? []
        const total = res?.pagination?.totalCount
        if (typeof total === "number" && total > rows.length) {
            console.warn(
                `[useTaskCategoryData] loaded ${rows.length}/${total} subtypes — pageSize 500 truncated the set; some subtypes are hidden.`,
            )
        }
        // 这次请求发出之后缓存被清过(登出/401)→ 它属于上一个会话,只回给当时的调用方,
        // 不进缓存。
        if (gen === subtypeCacheGen) subtypeCache = { rows, at: Date.now() }
        return rows
    })().finally(() => {
        // 失败时不写 cache，只清 in-flight → 下一次 mount 会重试（不缓存空结果）
        if (gen === subtypeCacheGen) subtypeInflight = null
    })
    return subtypeInflight
}

/**
 * 丢掉 subtype 缓存。
 *
 * 缓存的行里带 `tenantId`(租户自定义 subtype,是私有数据),而它活在模块级 ——
 * 比登录会话长。统一退出不 reload 页面,不清的话同一 tab 里下一个登入的用户(可能是
 * 另一个租户)会拿到上一个租户的 subtype 列表。
 * 401 与用户主动退出两条路都由下面那个 auth:unauthorized 监听覆盖(主动退出派发的是同一个
 * 事件,带 reason: 'sign_out',见 utils/authEvents)。
 * 测试同样用它(缓存跨用例存活)。
 */
export function clearTaskCategoryCache(): void {
    subtypeCache = null
    subtypeInflight = null
    // 在途请求作废(见 subtypeCacheGen):清完之后才落地的响应不再写回缓存。
    subtypeCacheGen += 1
}

/** @deprecated 用 `clearTaskCategoryCache`(同一个实现,名字不再只是测试用)。 */
export const __resetTaskCategoryCache = clearTaskCategoryCache

// 跨用户隔离(同 components/universal-list/viewsLookupCache.ts 范式)。
if (typeof window !== 'undefined') {
    window.addEventListener('auth:unauthorized', () => {
        clearTaskCategoryCache()
    })
}

/**
 * 表单（子类型）在后端改了（改名、停用、改建单表单）→ `task_subtypes` 配置版本变 → 清模块缓存、
 * 已挂载的实例重拉（TK6：以前只靠 5 分钟 TTL，详情页 / 建单页一直显示旧表单名）。
 */
const versionReloaders = new Set<() => void>()
onResourceVersionChange((resources) => {
    if (!resources.has("task_subtypes")) return
    clearTaskCategoryCache()
    versionReloaders.forEach((reload) => reload())
})

/**
 * Loads the data backing `TaskCategorySelect`:
 *
 * - task types from `useSystemLookup('task_type')`, with `special` / `*custom*`
 *   removed and an optional page-level `availableTypes` whitelist applied
 *   (case-insensitive) — mirrors the filtering the Create-Task form used to do
 *   inline for its task_type `<Select>`.
 * - ALL active subtypes in one `POST /task-subtypes/search` (no per-type filter,
 *   `inputFormSchema` column included), grouped by `taskTypeCode` in memory.
 *   The combo shows every type's subtypes at once, so a single fetch + in-memory
 *   group replaces the legacy "fetch per selected type" approach.
 *
 * Returns one `TaskCategoryGroup` per allowed type (preserving lookup order),
 * each carrying its icon/colors and its (possibly empty) subtype list.
 */
export function useTaskCategoryData(availableTypes?: string[]) {
    const { data: taskTypesData } = useLocalizedSystemLookup("task_type")
    const locale = useLocale()
    const [allSubtypes, setAllSubtypes] = useState<TaskSubtype[]>([])
    const [loading, setLoading] = useState(true)
    // 加载失败与「这个 type 没有 subtype」要能区分(IntentCreateForm 建单闸门据此禁用开关);
    // reload() 在同一个 mount 里重试(失败不进缓存,见 loadAllSubtypes)。
    const [error, setError] = useState(false)
    const [attempt, setAttempt] = useState(0)
    const reload = useCallback(() => setAttempt((n) => n + 1), [])
    // 版本重拉在已有数据时走后台(同 lookupSlice 009b0f09d 口径):不置 loading(建单页不闪加载态),
    // 失败保留旧列表、不写 error。首次加载、手动 reload() 照旧走前台。
    const hasDataRef = useRef(false)
    const backgroundRef = useRef(false)
    const versionReload = useCallback(() => {
        backgroundRef.current = true
        setAttempt((n) => n + 1)
    }, [])
    useEffect(() => {
        versionReloaders.add(versionReload)
        return () => {
            versionReloaders.delete(versionReload)
        }
    }, [versionReload])

    // Allowed task types. With an explicit whitelist, return exactly those types
    // (including `special` / `*custom*` when the caller asks for them — e.g. the
    // position work_type picker passes `["special"]`). Without a whitelist, keep
    // the default picker semantics: drop special/custom.
    const types = useMemo<TaskTypeOption[]>(() => {
        const all = (taskTypesData ?? []) as TaskTypeOption[]
        if (availableTypes && availableTypes.length > 0) {
            const whitelist = new Set(availableTypes.map((s) => s.toLowerCase()))
            return all.filter((t) => whitelist.has((t.code ?? "").toLowerCase()))
        }
        return all.filter((t) => t.code !== "special" && !t.code.includes("custom"))
    }, [taskTypesData, availableTypes])

    // Fetch every active subtype once. Subtype totals are small (single/low-double
    // digits per type); pageSize 500 covers all in one round-trip.
    useEffect(() => {
        let alive = true
        const background = backgroundRef.current && hasDataRef.current
        backgroundRef.current = false
        if (!background) {
            setLoading(true)
            setError(false)
        }

        const run = async () => {
            try {
                const rows = await loadAllSubtypes()
                if (!alive) return
                hasDataRef.current = rows.length > 0
                setAllSubtypes(rows)
            } catch (e) {
                if (!alive) return
                if (background) {
                    // 后台重拉失败:旧列表照旧可用,不清空、不报错;缓存已清,下次 mount / 版本变化再试。
                    console.warn("[useTaskCategoryData] background task-subtype refetch failed, keeping previous list:", e)
                    return
                }
                console.error("[useTaskCategoryData] task-subtype search failed:", e)
                hasDataRef.current = false
                setAllSubtypes([])
                setError(true)
            } finally {
                if (alive) setLoading(false)
            }
        }

        void run()
        return () => {
            alive = false
        }
    }, [attempt])

    // Group subtypes under each allowed type. Subtypes already arrive ordered by
    // sortOrder, so per-type slices stay ordered.
    const groups = useMemo<TaskCategoryGroup[]>(() => {
        // label 换成当前语言(canonicalLabel 留规范英文,存数据的地方用它),见 utils/subtypeLabel
        const byType = new Map<string, Localized<TaskSubtype>[]>()
        for (const raw of allSubtypes) {
            const sub = localizeSubtype(raw, locale)
            const list = byType.get(sub.taskTypeCode)
            if (list) list.push(sub)
            else byType.set(sub.taskTypeCode, [sub])
        }
        return types.map((t) => ({
            typeCode: t.code,
            typeLabel: t.label,
            icon: getTaskTypeIcon(t.code),
            iconBg: getTaskTypeIconBg(t.code),
            iconFg: getTaskTypeIconFg(t.code),
            subtypes: byType.get(t.code) ?? [],
        }))
    }, [types, allSubtypes, locale])

    return { groups, loading, error, reload }
}
